import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '@/App';
import { axios, API, authHdr, canOps } from './opsApi';
import {
  Users, MapPin, Truck, UserCog, Network, Search, X, Phone, Mail, Building2,
} from 'lucide-react';
import EmployeeProfileCard from '@/components/personnel/EmployeeProfileCard';
import { PersonnelDirectoryCard } from '@/components/personnel/PersonnelDirectoryCard';

const TABS = [
  { key: 'customers', label: 'Customers', icon: Users, cap: 'ops.customers.view' },
  { key: 'locations', label: 'Locations', icon: MapPin, cap: 'ops.locations.view' },
  { key: 'providers', label: 'Providers', icon: Truck, cap: 'ops.providers.view' },
  { key: 'personnel', label: 'Personnel', icon: UserCog, cap: 'ops.personnel.view' },
  { key: 'assignments', label: 'Assignments', icon: Network, cap: 'ops.assignments.view' },
];

const Pill = ({ children, tone = 'slate', ...rest }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-600', green: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700', red: 'bg-rose-100 text-rose-700',
    blue: 'bg-blue-100 text-blue-700',
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`} {...rest}>{children}</span>;
};

const Empty = ({ label }) => (
  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-400" data-testid="ops-dir-empty">{label}</div>
);

// Authoritative image thumbnail (customer logo / location photo). Reuses the existing
// Administration image endpoints with the ops user's token; falls back to a neutral icon
// tile when no image is on file — never a generic icon over a valid photograph.
const Thumb = ({ src, alt, Icon, testId }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400" data-testid={testId}>
        <Icon size={20} />
      </div>
    );
  }
  return <img src={src} alt={alt} onError={() => setErr(true)} className="h-12 w-12 shrink-0 rounded-lg bg-slate-100 object-cover" data-testid={testId} />;
};

// ---- Location detail workspace (drawer) -------------------------------------
const LocationDrawer = ({ loc, token, onClose }) => {
  if (!loc) return null;
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-[#0E2A47]">{value || '—'}</span>
    </div>
  );
  const oc = loc.operations_contact_name || loc.office_phone || loc.mobile_phone || loc.operations_contact_email;
  const notes = loc.operational_notes || loc.local_notes;
  const personnelCount = (loc.assigned_employee_ids || []).length;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="ops-location-drawer">
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Service Location</div>
            <h3 className="text-xl font-bold text-[#0E2A47]" data-testid="ops-location-drawer-title">{loc.location_name}</h3>
            <div className="text-sm text-slate-500">{loc.customer_name}</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" data-testid="ops-location-drawer-close"><X size={18} /></button>
        </div>
        {loc.image_url && token && (
          <img src={`${API}/service-locations/${loc.id}/image?variant=card&auth=${token}`} alt={loc.location_name}
            className="mb-4 h-44 w-full rounded-xl bg-slate-100 object-cover" onError={(e) => { e.target.style.display = 'none'; }} data-testid="ops-location-drawer-photo" />
        )}
        <div className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wide text-slate-400">Site</div>
        <Row label="Account / Code" value={[loc.account_number, loc.location_code].filter(Boolean).join(' · ')} />
        <Row label="Address" value={[loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')} />
        <Row label="County / Region" value={[loc.county, loc.region].filter(Boolean).join(' · ')} />
        <Row label="Service" value={[loc.service_frequency, loc.service_day, loc.service_time].filter(Boolean).join(' · ')} />
        <Row label="Status" value={loc.is_active === false ? 'Inactive' : 'Active'} />
        <Row label="Square Footage" value={loc.total_sq_ft ? `${Number(loc.total_sq_ft).toLocaleString()} sq ft` : null} />
        <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Assignments</div>
        <Row label="Assigned Personnel" value={personnelCount ? `${personnelCount} assigned` : null} />
        <Row label="Service Provider" value={loc.assigned_subcontractor_id ? 'Assigned' : null} />
        <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Operations Contact</div>
        {oc ? (
          <>
            <Row label="Name" value={loc.operations_contact_name} />
            <Row label="Office" value={loc.office_phone} />
            <Row label="Mobile" value={loc.mobile_phone} />
            <Row label="Email" value={loc.operations_contact_email} />
          </>
        ) : <div className="py-2 text-sm text-amber-600" data-testid="ops-location-no-opscontact">No operations contact designated</div>}
        {notes && (
          <>
            <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Operational Notes</div>
            <p className="whitespace-pre-wrap text-sm text-slate-600">{notes}</p>
          </>
        )}
      </div>
    </div>
  );
};

// ---- Service Provider detail workspace (drawer, read-only) ------------------
// Reuses the authoritative Service Provider Master projection. Qualification
// status (Registered/Pending vs Qualified Service Provider + QSP number) is shown
// distinctly from the operational Active/Inactive state. No approval controls.
const ProviderDrawer = ({ prov, onClose }) => {
  if (!prov) return null;
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-[#0E2A47]">{value || '—'}</span>
    </div>
  );
  const qualified = prov.qualification_status === 'qualified';
  const active = String(prov.status || 'active').toLowerCase() === 'active';
  const trades = (prov.specialty_trades || prov.trade_types || prov.specialties || []);
  const coverage = [...(prov.service_states || []), ...(prov.service_regions || []), ...(prov.service_counties || [])];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="ops-provider-drawer">
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Service Provider</div>
            <h3 className="text-xl font-bold text-[#0E2A47]" data-testid="ops-provider-drawer-title">{prov.company_name || 'Unnamed provider'}</h3>
            <div className="text-sm text-slate-500">{prov.provider_type || '—'}</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" data-testid="ops-provider-drawer-close"><X size={18} /></button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {qualified
            ? <Pill tone="green" data-testid="ops-provider-drawer-qual">Qualified Service Provider{prov.qsp_number ? ` · ${prov.qsp_number}` : ''}</Pill>
            : <Pill tone="amber" data-testid="ops-provider-drawer-qual">Registered / Pending</Pill>}
          <Pill tone={active ? 'blue' : 'slate'} data-testid="ops-provider-drawer-status">Operational: {active ? 'Active' : 'Inactive'}</Pill>
        </div>
        <div className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wide text-slate-400">Qualification</div>
        <Row label="Qualification status" value={qualified ? 'Qualified Service Provider' : 'Registered / Pending'} />
        <Row label="QSP number" value={prov.qsp_number} />
        <Row label="Date registered" value={prov.created_at ? String(prov.created_at).slice(0, 10) : null} />
        <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Primary Contact</div>
        <Row label="Name" value={prov.contact_name || [prov.contact_first_name, prov.contact_last_name].filter(Boolean).join(' ')} />
        <Row label="Telephone" value={prov.contact_phone} />
        <Row label="Email" value={prov.contact_email} />
        <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Trades & Coverage</div>
        <Row label="Specialty trades" value={trades.length ? trades.join(', ') : null} />
        <Row label="Geographic coverage" value={coverage.length ? coverage.join(', ') : null} />
        <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Compliance</div>
        <Row label="COI on file" value={prov.coi_on_file ? 'Yes' : 'No'} />
        <Row label="W-9 on file" value={prov.w9_on_file ? 'Yes' : 'No'} />
        <Row label="Insurance expiry" value={prov.insurance_expiry ? String(prov.insurance_expiry).slice(0, 10) : null} />
      </div>
    </div>
  );
};

function OperationsDirectory({ initialTab }) {
  const { user, token } = useContext(AuthContext);
  const allowed = TABS.filter((t) => canOps(user, t.cap));
  const [tab, setTab] = useState(
    initialTab && allowed.find((t) => t.key === initialTab) ? initialTab : (allowed[0]?.key || 'customers'),
  );
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [provDrawer, setProvDrawer] = useState(null);
  const [qualFilter, setQualFilter] = useState('');  // '' | qualified | registered_pending

  const reqId = React.useRef(0);
  const load = useCallback(async (term = q) => {
    if (!allowed.find((t) => t.key === tab)) return;
    const my = ++reqId.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tfs-ops/${tab}${term ? `?q=${encodeURIComponent(term)}` : ''}`, authHdr(token));
      if (my === reqId.current) setRows(Array.isArray(res.data) ? res.data : []);
    } catch { if (my === reqId.current) setRows([]); } finally { if (my === reqId.current) setLoading(false); }
  }, [tab, q, token, allowed]);

  // Reload whenever the tab changes; also reset the search box for the new tab.
  useEffect(() => { setQ(''); load(''); }, [tab]); // eslint-disable-line

  const openLocation = async (id) => {
    try { const res = await axios.get(`${API}/tfs-ops/locations/${id}`, authHdr(token)); setDrawer(res.data); } catch { /* noop */ }
  };

  if (allowed.length === 0) {
    return <div className="p-8"><Empty label="You do not have access to any Operations directory sections." /></div>;
  }

  return (
    <div className="p-6" data-testid="ops-directory">
      <h2 className="mb-1 text-2xl font-bold text-[#0E2A47]">Operations Directory</h2>
      <p className="mb-5 text-sm text-slate-500">Restricted operational records within your assigned scope.</p>

      <div className="mb-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="ops-dir-tabs">
        {allowed.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => { if (t.key === tab) load(''); else setTab(t.key); }}
              aria-pressed={active}
              className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors sm:justify-start sm:px-4 ${active
                ? 'border-[#0E2A47] bg-[#0E2A47] text-white shadow-sm'
                : 'border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-200'}`}
              data-testid={`ops-dir-tab-${t.key}`}>
              <t.icon size={16} className="shrink-0" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        <Search size={16} className="text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(q)}
          placeholder={`Search ${tab}…`} className="w-full bg-transparent text-sm outline-none" data-testid="ops-dir-search" />
        {q && <button onClick={() => { setQ(''); load(''); }} data-testid="ops-dir-search-clear"><X size={15} className="text-slate-400" /></button>}
      </div>

      {tab === 'providers' && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="ops-dir-provider-filters">
          <span className="text-xs font-semibold text-slate-500">Qualification:</span>
          {[['', 'All'], ['qualified', 'Qualified Service Provider'], ['registered_pending', 'Registered / Pending']].map(([val, label]) => (
            <button key={val || 'all'} onClick={() => setQualFilter(val)} data-testid={`ops-dir-qualfilter-${val || 'all'}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${qualFilter === val ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{label}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="ops-dir-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty label={`No ${tab} in your scope.`} />
      ) : (
        <div className={tab === 'personnel' ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3' : 'overflow-hidden rounded-xl border border-slate-200 bg-white'} data-testid={`ops-dir-list-${tab}`}>
          {tab === 'customers' && rows.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-0" data-testid={`ops-dir-customer-${c.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Thumb src={c.logo_url ? `${API}/customers/${c.id}/logo?auth=${token}` : null} alt={c.name} Icon={Building2} testId={`ops-dir-customer-logo-${c.id}`} />
                <div className="min-w-0">
                  <div className="truncate font-semibold text-[#0E2A47]">{c.name}</div>
                  <div className="truncate text-xs text-slate-500">#{c.account_number || '—'}{c.account_code ? ` · ${c.account_code}` : ''} · {c.industry || c.type || '—'}</div>
                  {c.total_locations != null && <div className="text-xs text-slate-500">{c.total_locations} service location{c.total_locations === 1 ? '' : 's'}</div>}
                  <div className="mt-1 text-xs text-slate-500">{c.operations_contact ? (<span className="inline-flex items-center gap-1"><Phone size={11} />{c.operations_contact.name || 'contact'} {c.operations_contact.office_phone || c.operations_contact.mobile_phone || ''}</span>) : <span className="text-amber-600">No operations contact designated</span>}</div>
                </div>
              </div>
              <Pill tone={c.status === 'active' ? 'green' : 'slate'}>{c.status || '—'}</Pill>
            </div>
          ))}
          {tab === 'locations' && rows.map((l) => (
            <button key={l.id} onClick={() => openLocation(l.id)} className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 last:border-0" data-testid={`ops-dir-location-${l.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Thumb src={l.image_url ? `${API}/service-locations/${l.id}/image?variant=card&auth=${token}` : null} alt={l.location_name} Icon={Building2} testId={`ops-dir-location-photo-${l.id}`} />
                <div className="min-w-0">
                  <div className="truncate font-semibold text-[#0E2A47]">{l.location_name}</div>
                  <div className="truncate text-xs text-slate-500">{l.customer_name} · {[l.city, l.state].filter(Boolean).join(', ') || '—'}</div>
                  {(l.location_code || l.total_sq_ft) && <div className="truncate text-xs text-slate-400">{l.location_code ? `#${l.location_code}` : ''}{l.location_code && l.total_sq_ft ? ' · ' : ''}{l.total_sq_ft ? `${Number(l.total_sq_ft).toLocaleString()} sq ft` : ''}</div>}
                </div>
              </div>
              <Pill tone={l.is_active === false ? 'slate' : 'green'}>{l.is_active === false ? 'Inactive' : 'Active'}</Pill>
            </button>
          ))}
          {tab === 'providers' && (() => {
            const fp = rows.filter((p) => !qualFilter || (p.qualification_status || 'registered_pending') === qualFilter);
            if (fp.length === 0) return <div className="p-4"><Empty label="No providers match this qualification filter." /></div>;
            return fp.map((p) => {
            const active = String(p.status || 'active').toLowerCase() === 'active';
            const trades = (p.specialty_trades || p.trade_types || p.specialties || []);
            const coverage = [...(p.service_states || []), ...(p.service_regions || []), ...(p.service_counties || [])];
            const contact = p.contact_name || [p.contact_first_name, p.contact_last_name].filter(Boolean).join(' ');
            return (
              <button key={p.id} type="button" onClick={() => setProvDrawer(p)}
                className="flex w-full items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 last:border-0" data-testid={`ops-dir-provider-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate font-semibold text-[#0E2A47]">{p.company_name || 'Unnamed provider'}</span>
                    {p.qsp_number && <span className="rounded bg-[#0E2A47] px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`ops-dir-provider-qsp-${p.id}`}>{p.qsp_number}</span>}
                  </div>
                  <div className="truncate text-xs text-slate-500">{p.provider_type || '—'} · {trades.slice(0, 3).join(', ') || '—'}</div>
                  <div className="mt-0.5 truncate text-xs text-slate-400">
                    {contact ? <span className="inline-flex items-center gap-1"><Phone size={10} />{contact}{p.contact_phone ? ` · ${p.contact_phone}` : ''}</span> : 'No primary contact'}
                    {coverage.length ? <span> · {coverage.slice(0, 3).join(', ')}</span> : null}
                    {p.created_at ? <span> · Registered {String(p.created_at).slice(0, 10)}</span> : null}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {p.qualification_status === 'qualified'
                      ? <Pill tone="green" data-testid={`ops-dir-provider-qual-${p.id}`}>Qualified Service Provider</Pill>
                      : <Pill tone="amber" data-testid={`ops-dir-provider-qual-${p.id}`}>Registered / Pending</Pill>}
                    {p.coi_on_file && <Pill tone="slate">COI</Pill>}
                    {p.w9_on_file && <Pill tone="slate">W-9</Pill>}
                    {p.insurance_expiry && <Pill tone="amber">Ins exp {String(p.insurance_expiry).slice(0, 10)}</Pill>}
                  </div>
                </div>
                <Pill tone={active ? 'blue' : 'slate'} data-testid={`ops-dir-provider-status-${p.id}`}>Operational: {active ? 'Active' : 'Inactive'}</Pill>
              </button>
            );
          });
          })()}
          {tab === 'personnel' && rows.map((p) => (
            <PersonnelDirectoryCard key={p.id} p={p} token={token} onOpen={setProfileId} />
          ))}
          {tab === 'assignments' && rows.map((a) => (
            <div key={a.location_id} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 last:border-0" data-testid={`ops-dir-assignment-${a.location_id}`}>
              <div className="min-w-0">
                <div className="truncate font-semibold text-[#0E2A47]">{a.location_name}</div>
                <div className="text-xs text-slate-500">{a.customer_name}</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Users size={12} />{(a.assigned_personnel_ids || []).length}</span>
                {a.subcontractor_id ? <Pill tone="green">Sub</Pill> : <Pill tone="slate">No sub</Pill>}
              </div>
            </div>
          ))}
        </div>
      )}
      <LocationDrawer loc={drawer} token={token} onClose={() => setDrawer(null)} />
      <ProviderDrawer prov={provDrawer} onClose={() => setProvDrawer(null)} />
      {profileId && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-100" data-testid="ops-personnel-profile-overlay">
          <div className="mx-auto max-w-7xl px-4 py-6">
            <EmployeeProfileCard personnelId={profileId} onClose={() => setProfileId(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default OperationsDirectory;
